import first from 'lodash/fp/first';
import isNil from 'lodash/fp/isNil';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Unit } from '../../../common/constants';
import { getBatteryLevelPercentage } from '../../../common/tourUtils';
import { convertISOToRelativeDate, getBatteryColor, getBatteryIcon } from '../../../common/utils';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { trace } from '../../../configuration/setup/trace';
import { resolveAddress } from '../../../features/fetchData/resolveAddress';
import { TimelineEventType } from '../../../store/facade/facadeApiV3';
import { selectIsFirstWaypointVehicleLocation } from '../../../store/search/searchSelectors';
import { selectLocale } from '../../../store/lang/langSelectors';
import type { Itinerary } from '../../../store/tour/tourSlice';
import { selectBatteryCapacity } from '../../../store/vehicleProfile/vehicleProfileSelectors';

export type DeparturePanelProps = {
    cargoAtDeparture?: number;
    itineraries?: Itinerary[];
    selectedItinerary?: number;
};

export const DeparturePanel = ({ cargoAtDeparture, itineraries, selectedItinerary }: DeparturePanelProps) => {
    const dispatch = useAppDispatch();
    const isFirstWpVehicleLocation = useAppSelector(selectIsFirstWaypointVehicleLocation);
    const locale = useAppSelector(selectLocale);
    const batteryCapacity = useAppSelector(selectBatteryCapacity).value;
    const [vehicleAddress, setVehicleAddress] = useState<string>();

    const currentItinerary = selectedItinerary !== undefined ? itineraries?.at(selectedItinerary) : undefined;

    const departureEvent = currentItinerary?.events?.timelineEvents?.find(
        e => e.timelineEventType === TimelineEventType.Departure
    );
    const departureAddress =
        departureEvent?.timelineEventType === TimelineEventType.Departure
            ? (departureEvent.address?.name ?? departureEvent.address?.street)
            : undefined;
    const departureCoordinates =
        departureEvent?.timelineEventType === TimelineEventType.Departure ? departureEvent.coordinates : undefined;

    const departureTime = currentItinerary?.sections?.at(0)?.departureTime;
    const batteryPct = getBatteryLevelPercentage(
        batteryCapacity,
        first(currentItinerary?.sections)?.evBatteryCapacityDeparture
    );

    const date = convertISOToRelativeDate(locale, departureTime);

    const getVehicleLocation = async () => {
        try {
            const eventObj = {
                type: 'EVENT_SMART_ROUTING',
                payload: { coordinates: departureCoordinates },
            };
            const resolvedAddress = await dispatch(resolveAddress(eventObj));
            setVehicleAddress(resolvedAddress);
        } catch (error) {
            trace.error('Could not resolve vehicle address: ', error);
        }
    };

    useEffect(() => {
        if (isFirstWpVehicleLocation) {
            getVehicleLocation();
        }
    });

    return (
        <li className='timeline-element' data-testid='timeline-element'>
            <div className='timeline-date margin-right-10'>
                <div className='border border-width-3 border-color-white'>
                    <span className='badge badge-lg badge-muted padding-5'>
                        <span className='rioglyph rioglyph-start' />
                    </span>
                </div>
            </div>
            <div className='timeline-content margin-bottom-15'>
                <div className='text-medium text-size-16 text-color-darker margin-top-5 margin-bottom-5'>
                    <FormattedMessage id='intl-msg:smartRoute.tour.departure' />
                </div>
                <div className='display-flex flex-column gap-2'>
                    {date && (
                        <div>
                            <span className='rioglyph rioglyph-calendar text-color-gray margin-right-5' />
                            <span>{date}</span>
                        </div>
                    )}
                    <div>
                        <span className='rioglyph rioglyph-map-marker text-color-gray margin-right-5' />
                        <span>{isFirstWpVehicleLocation ? vehicleAddress : departureAddress}</span>
                    </div>
                    {!isNil(cargoAtDeparture) && (
                        <div>
                            <span className='rioglyph rioglyph-weight text-color-gray margin-right-5' />
                            <span>{cargoAtDeparture}</span>
                            <span>&nbsp;{Unit.weight}</span>
                        </div>
                    )}
                    {!isNil(batteryPct) && (
                        <div className={`${getBatteryColor(batteryPct)}`}>
                            <span className={`${getBatteryIcon(batteryPct)} margin-right-5`} />
                            <span>{batteryPct}</span>
                            <span>&nbsp;{Unit.pct}</span>
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};
